import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import auth from 'auth'
import useAxios from 'hooks/useAxios'
import { mcpOauthPendingConnection } from 'libs/mcpOauthPendingConnection'
import { replaceLocation } from 'libs/replaceLocation'

import OAuthConnect from './index'
import { ConnectScreen, ConnectStep } from './types'
import { useHasWaitedLong } from './useHasWaitedLong'

const CONNECT_PATHNAME = '/oauth/connect/'
const LOGIN_PATHNAME = '/login/'
const WORKSPACES_PATHNAME = '/workspaces/'
const AI_APP_NAME = 'your AI app'

interface ConnectLocationState {
  saveUserActivityLog?: { action?: string }
}

interface CompletionError {
  response?: { status?: number; data?: { code?: string } }
}

function isLoggedOutByInterceptor(error: CompletionError) {
  return error.response?.status === 401 && !auth.isLoggedIn()
}

function toScreen(
  step: ConnectStep | null,
  isConnecting: boolean,
  hasWaitedLong: boolean
): ConnectScreen | null {
  if (isConnecting && hasWaitedLong) {
    return { kind: 'connecting' }
  }
  if (step?.kind === 'confirm') {
    return { ...step, isConnecting }
  }
  return step
}

function toFailedStep(error: CompletionError): ConnectStep {
  const { status, data } = error.response ?? {}
  return status === 404 && data?.code === 'no_workspace'
    ? { kind: 'no-workspaces' }
    : { kind: 'error' }
}

export default function OAuthConnectContainer() {
  const location = useLocation<ConnectLocationState | undefined>()
  const history = useHistory()

  const [step, setStep] = useState<ConnectStep | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const { hasWaitedLong } = useHasWaitedLong(isConnecting)
  const externalAuthIdRef = useRef<string | null>(null)
  const pendingExpiredAtRef = useRef<number | undefined>(undefined)
  const hasInitializedRef = useRef(false)

  const { fetch: completeConnection } = useAxios({
    method: 'post',
    url: 'account/mcp-oauth/complete/',
    skip: true,
  })

  const connect = useCallback(() => {
    const externalAuthId = externalAuthIdRef.current
    if (!externalAuthId) {
      return
    }
    const expiredAt = pendingExpiredAtRef.current
    setIsConnecting(true)
    completeConnection({ data: { external_auth_id: externalAuthId } })
      .then((response: { data: { redirect_uri: string } }) => {
        mcpOauthPendingConnection.clear()
        replaceLocation(response.data.redirect_uri)
      })
      .catch((error: CompletionError) => {
        if (isLoggedOutByInterceptor(error)) {
          mcpOauthPendingConnection.save(externalAuthId, expiredAt)
          return
        }
        mcpOauthPendingConnection.clear()
        setIsConnecting(false)
        setStep(toFailedStep(error))
      })
  }, [completeConnection])

  useEffect(() => {
    if (hasInitializedRef.current) {
      return
    }
    hasInitializedRef.current = true

    const externalAuthId = new URLSearchParams(location.search).get('external_auth_id')
    if (externalAuthId) {
      mcpOauthPendingConnection.save(externalAuthId)
      history.replace({ pathname: CONNECT_PATHNAME, search: '', state: location.state })
    }

    if (!auth.isLoggedIn()) {
      history.replace({
        pathname: LOGIN_PATHNAME,
        search: '',
        state: { prev: { pathname: CONNECT_PATHNAME } },
      })
      return
    }

    const pendingEntry = mcpOauthPendingConnection.read()
    if (!pendingEntry || auth.getUserInfoFromToken()?.assumed_by) {
      mcpOauthPendingConnection.clear()
      setStep({ kind: 'error' })
      return
    }

    externalAuthIdRef.current = pendingEntry.externalAuthId
    pendingExpiredAtRef.current = pendingEntry.expiredAt

    if (location.state?.saveUserActivityLog?.action === 'signed_in') {
      connect()
      return
    }
    setStep({ kind: 'confirm', email: auth.getUserInfoFromToken()?.email ?? '' })
  }, [connect, history, location.search, location.state])

  useEffect(
    () => () => {
      if (externalAuthIdRef.current && !auth.isLoggedIn()) {
        mcpOauthPendingConnection.save(externalAuthIdRef.current, pendingExpiredAtRef.current)
      }
    },
    []
  )

  const switchAccount = () => {
    if (isConnecting) {
      return
    }
    auth.logout()
    history.replace({
      pathname: LOGIN_PATHNAME,
      state: { prev: { pathname: CONNECT_PATHNAME } },
    })
  }

  return (
    <OAuthConnect
      screen={toScreen(step, isConnecting, hasWaitedLong)}
      aiAppName={AI_APP_NAME}
      onContinue={() => !isConnecting && connect()}
      onSwitchAccount={switchAccount}
      onCreateWorkspace={() => history.push(WORKSPACES_PATHNAME)}
    />
  )
}



// WEBPACK FOOTER //
// ./src/pages/OAuthConnect/container.tsx